import { createFileRoute } from '@tanstack/react-router';
import { useAction, useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import type { Id } from 'convex/_generated/dataModel';
import React from 'react';
import i18n from '@/i18n';
import { isInvoiceConfigurationRequiredError } from '@/lib/payment-errors';

type ProviderBillingSearch = {
  applicationId?: string;
  invalidApplicationId?: boolean;
};

const CONVEX_ID_PATTERN = /^[a-z0-9]{10,80}$/;

const parseApplicationId = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (!CONVEX_ID_PATTERN.test(trimmed)) {
    return null;
  }

  return trimmed;
};

const ProviderBillingPage = () => {
  const { dutyId } = Route.useParams();
  const { applicationId, invalidApplicationId } = Route.useSearch();
  const validatedApplicationId = applicationId as Id<'applications'> | undefined;
  const startProviderBooking = useAction(api.app.payments.platformFeeActions.startProviderBooking as any);
  const duty = useQuery(api.app.duties.queries.getDutyPublic as any, { dutyId }) as any;
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onPay = async () => {
    if (!validatedApplicationId) {
      setError(invalidApplicationId ? 'Ungültige Bewerbungs-ID. Bitte starten Sie die Bezahlung erneut über die Dienstdetailseite.' : 'Es fehlt eine Bewerbungs-ID. Bitte öffnen Sie die Bezahlung über die Dienstdetailseite.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const result = await startProviderBooking({
        serviceId: dutyId,
        applicationId: validatedApplicationId,
      });

      if (result.mode === 'checkout') {
        window.location.href = result.checkoutUrl;
        return;
      }

      window.location.href = `/provider/service/${dutyId}`;
    } catch (err) {
      if (isInvoiceConfigurationRequiredError(err)) {
        setError(i18n.t('common:toasts.platformFee.invoiceSettingsMissing'));
      } else {
        setError(err instanceof Error ? err.message : 'Bezahlung konnte nicht gestartet werden.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='max-w-xl mx-auto p-6 space-y-4'>
      <h1 className='text-2xl font-semibold'>Bezahlung</h1>
      <div className='text-sm opacity-80'>
        Dienst-ID: <span className='font-mono'>{dutyId}</span>
      </div>
      {duty ? (
        <div className='text-sm space-y-1'>
          <div className='font-medium'>{duty.title}</div>
          <div>
            {duty.location?.city || '—'} • {duty.specialty || '—'}
          </div>
          <div>
            Honorar:
            {duty.compensationType === 'range' ? ` € ${duty.minPrice}–${duty.maxPrice ?? duty.minPrice}` : ` € ${duty.minPrice}`}
          </div>
        </div>
      ) : (
        <div className='text-sm text-muted-foreground'>Dienstdaten werden geladen…</div>
      )}
      {!validatedApplicationId ? (
        <div className='text-sm text-muted-foreground space-y-2'>
          <p>{invalidApplicationId ? 'Die Bewerbungs-ID in der URL ist ungültig.' : 'Diese Seite benötigt eine Bewerbungs-ID für den Buchungsvorgang.'}</p>
          <Button
            variant='outline'
            onClick={() => {
              window.location.href = `/provider/service/${dutyId}`;
            }}
          >
            Zur Dienstdetailseite
          </Button>
        </div>
      ) : (
        <div className='pt-2'>
          <Button onClick={onPay} disabled={isSubmitting}>
            {isSubmitting ? 'Weiterleitung…' : 'Zur Kasse'}
          </Button>
        </div>
      )}
      {error ? <div className='text-sm text-destructive'>{error}</div> : null}
    </div>
  );
};

export const Route = createFileRoute('/provider/billing/$dutyId')({
  validateSearch: (search: Record<string, unknown>): ProviderBillingSearch => {
    const raw = search['applicationId'] ?? search['application_id'];
    const applicationId = parseApplicationId(raw);
    if (applicationId) {
      return { applicationId };
    }

    if (typeof raw === 'string' && raw.trim().length > 0) {
      return { invalidApplicationId: true };
    }

    return {};
  },
  component: ProviderBillingPage,
});
